import { Badge, Button, toast } from "@devhop/ui";
import {
  HardDrive,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import {
  ensurePersistentStorage,
  getPersistentStorageContext,
  getPersistentStorageGuidance,
  getStorageEstimate,
  type PersistentStorageContext,
} from "@/shared/lib/persistent-storage";

type StorageStatus = PersistentStorageContext & {
  usage?: number;
  quota?: number;
};

function formatBytes(bytes?: number): string {
  if (bytes === undefined) return "—";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(1)} ${units[unitIndex]}`;
}

export function PersistentStorageCard() {
  const [status, setStatus] = useState<StorageStatus | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [context, estimate] = await Promise.all([
        getPersistentStorageContext(),
        getStorageEstimate(),
      ]);
      setStatus({
        ...context,
        usage: estimate.usage,
        quota: estimate.quota,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleEnable = useCallback(async () => {
    setLoading(true);
    try {
      const result = await ensurePersistentStorage();
      await refresh();
      if (result.persisted) {
        toast.success("ໄດ້ຮັບສິດເກັບຂໍ້ມູນຖາວອນແລ້ວ");
        return;
      }
      toast.warning(getPersistentStorageGuidance(result.guidanceKey), {
        duration: 8000,
      });
    } finally {
      setLoading(false);
    }
  }, [refresh]);

  const persisted = status?.persisted ?? false;
  const supported = status?.supported ?? false;
  const guidance = status
    ? getPersistentStorageGuidance(status.guidanceKey)
    : null;
  const usagePercent =
    status?.usage !== undefined &&
    status?.quota !== undefined &&
    status.quota > 0
      ? Math.min(100, (status.usage / status.quota) * 100)
      : null;

  return (
    <div className="rounded-lg border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b p-4">
        <div className="flex items-center gap-2">
          <HardDrive className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-semibold">ການເກັບຂໍ້ມູນຖາວອນ</h2>
        </div>
        <Badge variant={persisted ? "default" : "destructive"}>
          {persisted ? "ປອດໄພ (persisted)" : "ມີຄວາມສ່ຽງເສຍຂໍ້ມູນ"}
        </Badge>
      </div>

      <div className="space-y-3 p-4">
        {!supported ? (
          <div className="flex items-start gap-2 rounded-md bg-destructive/10 p-3 text-destructive text-sm">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              ເບຣາວເຊີ/ອຸປະກອນນີ້ບໍ່ຮອງຮັບ Persistent Storage API — ຂໍ້ມູນ
              offline ອາດຖືກລຶບເມື່ອປິດເຄື່ອງ ແນະນຳໃຫ້ຕິດຕັ້ງເປັນ PWA ຫຼື
              ຕັ້ງຄ່າ WebView ໃຫ້ເກັບຂໍ້ມູນຖາວອນ
            </span>
          </div>
        ) : persisted ? (
          <div className="flex items-start gap-2 rounded-md bg-green-600/10 p-3 text-green-700 text-sm dark:text-green-400">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              ອຸປະກອນນີ້ໄດ້ຮັບສິດເກັບຂໍ້ມູນຖາວອນແລ້ວ — order offline ໃນ
              IndexedDB ຈະບໍ່ຖືກລຶບເມື່ອປິດ/ຣີບູດເຄື່ອງ
            </span>
          </div>
        ) : (
          <div className="flex items-start gap-2 rounded-md bg-destructive/10 p-3 text-destructive text-sm">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              {guidance ??
                "ອຸປະກອນນີ້ຍັງບໍ່ໄດ້ຮັບສິດເກັບຂໍ້ມູນຖາວອນ — ກົດປຸ່ມຂ້າງລຸ່ມເພື່ອລອງຂໍ (ເບຣາວເຊີອາດປະຕິເສດຖ້າເປີດຜ່ານແທັບປົກກະຕິ)"}
            </span>
          </div>
        )}

        {!persisted && status ? (
          <div className="rounded-md border bg-muted/40 p-3 text-muted-foreground text-xs">
            <div>
              ໂໝດ: {status.isStandalone ? "PWA / standalone" : "ແທັບເບຣາວເຊີ"}
            </div>
            <div>
              HTTPS: {status.isSecureContext ? "ໃຊ້ໄດ້" : "ບໍ່ໃຊ້ໄດ້"}
            </div>
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <div className="text-muted-foreground text-xs">ໃຊ້ແລ້ວ</div>
            <div className="font-medium">{formatBytes(status?.usage)}</div>
          </div>
          <div>
            <div className="text-muted-foreground text-xs">ໂຄງຕາ</div>
            <div className="font-medium">{formatBytes(status?.quota)}</div>
          </div>
        </div>

        {usagePercent !== null ? (
          <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full bg-primary"
              style={{ width: `${usagePercent}%` }}
            />
          </div>
        ) : null}

        <div className="flex flex-wrap gap-2 pt-1">
          <Button variant="outline" onClick={refresh} disabled={loading}>
            <RefreshCw className="mr-2 h-4 w-4" />
            ກວດສອບໃໝ່
          </Button>
          {supported && !persisted ? (
            <Button onClick={handleEnable} disabled={loading}>
              <ShieldCheck className="mr-2 h-4 w-4" />
              ຂໍສິດເກັບຂໍ້ມູນຖາວອນ
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
